/**
 * @fileoverview TanStack Query hooks for MCP personal access tokens.
 * @module features/mcp/hooks/useMcpTokens
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type CreateMcpTokenRequest,
  issueMcpToken,
  listMcpTokens,
  revokeMcpToken,
} from '../api/mcpTokenService'
import type { IssuedMcpToken, McpToken } from '../schemas/mcpToken'

/** Query key for the current user's MCP tokens list. */
export const MCP_TOKENS_QUERY_KEY = ['mcp-tokens'] as const

/**
 * Fetches the list of MCP tokens belonging to the current user.
 */
export function useMcpTokens(): ReturnType<typeof useQuery<McpToken[]>> {
  return useQuery<McpToken[]>({
    queryKey: MCP_TOKENS_QUERY_KEY,
    queryFn: () => listMcpTokens(),
  })
}

/**
 * Issues a new MCP token. Returned token contains the plaintext value once.
 */
export function useIssueMcpToken(): ReturnType<
  typeof useMutation<IssuedMcpToken, Error, CreateMcpTokenRequest>
> {
  const queryClient = useQueryClient()
  return useMutation<IssuedMcpToken, Error, CreateMcpTokenRequest>({
    mutationFn: (body) => issueMcpToken(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY })
    },
  })
}

/**
 * Revokes an MCP token by id.
 */
export function useRevokeMcpToken(): ReturnType<typeof useMutation<void, Error, string>> {
  const queryClient = useQueryClient()
  return useMutation<void, Error, string>({
    mutationFn: (id) => revokeMcpToken(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY })
    },
  })
}
